import dayjs from 'dayjs'
import { getFDRMinimumPayment } from '../../../config/consts'
import { formatMoney } from '../../../utils/amount'

// Mirrors the server's calculateProgramFeeAmount. A flat fee is a fixed number and must not move
// when the debt total is corrected — that is the whole reason it is a shape of its own rather than
// a percentage that happens to work out to the right figure. Without the flat branch a flat client's
// on-screen total read as a share of their debt, and with programFee defaulting to 50, as half of it.
export const calculateProgramFeeAmount = ({ accountBalance, programPlan }) => {
  if (programPlan?.feeType === 'flat') return Number(programPlan?.flatFee) || 0

  return accountBalance / 100 * programPlan?.programFee
}

export const getPaymentCount = ({ programLength, frequency, paymentDays }) => {
  switch (frequency) {
    case 'semiMonthly':
      return programLength * 2
    case 'custom':
      return programLength * (paymentDays?.length || 1)
    default:
      return programLength
  }
}

export const calculateMonthCount = ({
  programLength,
  frequency,
  startDate,
  secondStartDate,
  paymentDays,
  startFromMonth,
}) => {
  if (!startDate || !programLength || programLength <= 0) return null

  const start = dayjs(startDate)
  if (!start.isValid()) return null

  if (frequency === 'custom' && (!paymentDays || paymentDays.length === 0 || !startFromMonth)) {
    return null
  }

  const totalPayments = getPaymentCount({ programLength, frequency, paymentDays })
  const months = new Set()
  const second = secondStartDate ? dayjs(secondStartDate) : null

  for (let i = 0; i < totalPayments; i++) {
    let dueDate

    switch (frequency) {
      case 'weekly':
        dueDate = start.add(i, 'week')
        break
      case 'every2':
      case 'biweekly':
        dueDate = start.add(14 * i, 'day')
        break
      case 'semiMonthly': {
        const inc = Math.floor(i / 2)
        dueDate = i % 2 === 0
          ? start.add(inc, 'month')
          : (second && second.isValid() ? second.add(inc, 'month') : start.add(15, 'day').add(inc, 'month'))
        break
      }
      case 'custom': {
        const inc = Math.floor(i / paymentDays.length)
        const paymentDay = paymentDays[i % paymentDays.length]
        const baseYear = start.year()
        const currentMonthIdx = start.month() + 1
        const year = startFromMonth < currentMonthIdx ? baseYear + 1 : baseYear
        const monthStr = String(startFromMonth).padStart(2, '0')
        const dayStr = String(paymentDay).padStart(2, '0')
        dueDate = dayjs(`${year}-${monthStr}-${dayStr}`).add(inc, 'month')
        break
      }
      case 'monthly':
      default:
        dueDate = start.add(i, 'month')
        break
    }

    months.add(dueDate.format('YYYY-MM'))
  }

  return months.size
}

export const calculateTotalAmount = ({
  accountBalance,
  programPlan,
  programLength = 0,
  frequency = 'monthly',
  startDate,
  secondStartDate,
}) => {
  const programFeeAmount = calculateProgramFeeAmount({ accountBalance, programPlan })

  const length = programLength || programPlan?.plan
  const freq = frequency || programPlan?.frequency || 'monthly'

  const monthCount = calculateMonthCount({
    programLength: length,
    frequency: freq,
    startDate,
    secondStartDate,
    paymentDays: programPlan?.paymentDays,
    startFromMonth: programPlan?.startFromMonth,
  })

  if (monthCount === null) return null

  return programFeeAmount + (programPlan?.custodialFee * monthCount)
}

export const calculateMonthlyPaymentAmount = ({
  accountBalance,
  programPlan,
  programLength = 0,
  frequency = 'monthly',
  startDate,
  secondStartDate,
}) => {
  const amount = calculateTotalAmount({
    accountBalance,
    programPlan,
    programLength,
    frequency,
    startDate,
    secondStartDate,
  })

  if (amount === null) return null

  const length = programLength || programPlan?.plan
  const freq = frequency || programPlan?.frequency || 'monthly'

  return amount / getPaymentCount({
    programLength: length,
    frequency: freq,
    paymentDays: programPlan?.paymentDays,
  })
}

export const validateMinimumMonthlyPayment = ({
  accountBalance,
  programPlan,
  programLength,
  frequency,
  startDate,
  secondStartDate,
  onePercentFloor = false,
}) => {
  const minimumRequired = getFDRMinimumPayment(accountBalance, { onePercentFloor })

  const paymentPerPeriod = calculateMonthlyPaymentAmount({
    accountBalance,
    programPlan,
    programLength,
    frequency,
    startDate,
    secondStartDate,
  })

  if (paymentPerPeriod === null) return true

  let monthlyEquivalent = paymentPerPeriod

  switch (frequency) {
    case 'weekly':
      monthlyEquivalent = paymentPerPeriod * (52 / 12)
      break
    case 'every2':
    case 'biweekly':
      monthlyEquivalent = paymentPerPeriod * (26 / 12)
      break
    case 'semiMonthly':
      monthlyEquivalent = paymentPerPeriod * 2
      break
    case 'monthly':
      monthlyEquivalent = paymentPerPeriod
      break
    case 'custom':
      monthlyEquivalent = paymentPerPeriod
      break
    default:
      monthlyEquivalent = paymentPerPeriod
  }

  return monthlyEquivalent >= minimumRequired
}

export const paymentScheduleEntryMode = ({ recalcRequested, isCreate }) => {
  if (recalcRequested) return 'recompute'
  if (!isCreate) return 'load-saved'
  return 'await-input'
}

export const lockScheduleAndReviewTabs = (disableTabs) => ({
  ...disableTabs,
  paymentSchedule: true,
  review: true,
})

export const toSavedCreditProfileIds = (selectedCreditProfiles) => {
  if (!Array.isArray(selectedCreditProfiles)) return []
  return selectedCreditProfiles
    .map((profile) => (typeof profile === 'string' ? profile : profile?._id))
    .filter(Boolean)
}

export const sumSelectedBalances = (selectedCreditLines) => selectedCreditLines?.reduce((total, item) => {
  return total + (item.balanceAmount || 0)
}, 0)

export const formatEnrolledTotal = (selectedCreditLines) => formatMoney(sumSelectedBalances(selectedCreditLines) ?? 0)

export const isCreditSelectionPersisted = ({ selectedCreditLines, savedCreditProfileIds }) => {
  const saved = toSavedCreditProfileIds(savedCreditProfileIds)
  if (saved.length === 0) return false

  const live = toSavedCreditProfileIds(selectedCreditLines)
  if (live.length !== saved.length) return false

  const savedSet = new Set(saved)
  return live.every((id) => savedSet.has(id))
}

export const pickProgramPlanTier = ({ tiers, totalDebt }) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return null
  const amount = Number(totalDebt) || 0
  for (const tier of tiers) {
    if (tier?.maxAmount === null || tier?.maxAmount === undefined) return tier
    const max = Number(tier.maxAmount)
    if (Number.isFinite(max) && amount <= max) return tier
  }
  return null
}

